import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import {
  createEmptyBrief,
  type ChapterGateReview,
  type ThesisBrief,
} from '@/lib/thesisFramework/types'
import { deriveStage } from '@/lib/thesisFramework/briefReadiness'
import { unlockNextChapter } from '@/lib/thesisFramework/chapterAccess'

type State = {
  byNovelId: Record<string, ThesisBrief>
  getBrief: (novelId: string) => ThesisBrief
  ensureBrief: (novelId: string, seed?: Partial<ThesisBrief>) => ThesisBrief
  updateBrief: (novelId: string, patch: Partial<ThesisBrief>) => void
  setChapterDrafting: (novelId: string, chapterIndex: number) => void
  applyGateResult: (
    novelId: string,
    chapterIndex: number,
    review: ChapterGateReview,
    summaryIfPass: string,
  ) => void
  resetBrief: (novelId: string) => void
}

export const useThesisBriefStore = create<State>()(
  persist(
    (set, get) => ({
      byNovelId: {},
      getBrief: (novelId) => {
        return get().byNovelId[novelId] || createEmptyBrief(novelId)
      },
      ensureBrief: (novelId, seed) => {
        const existing = get().byNovelId[novelId]
        if (existing) {
          if (seed) {
            const merged = {
              ...existing,
              ...seed,
              novelId,
              updatedAt: new Date().toISOString(),
            }
            merged.stage = deriveStage(merged)
            set({ byNovelId: { ...get().byNovelId, [novelId]: merged } })
            return merged
          }
          return existing
        }
        const brief = { ...createEmptyBrief(novelId), ...seed, novelId }
        brief.stage = deriveStage(brief)
        set({ byNovelId: { ...get().byNovelId, [novelId]: brief } })
        return brief
      },
      updateBrief: (novelId, patch) => {
        const cur = get().getBrief(novelId)
        const next: ThesisBrief = {
          ...cur,
          ...patch,
          novelId,
          updatedAt: new Date().toISOString(),
          chapterGates: patch.chapterGates || cur.chapterGates,
        }
        next.stage = deriveStage(next)
        set({ byNovelId: { ...get().byNovelId, [novelId]: next } })
      },
      setChapterDrafting: (novelId, chapterIndex) => {
        const cur = get().getBrief(novelId)
        const key = String(chapterIndex)
        const g = cur.chapterGates[key] || { status: 'open' as const, summary: '' }
        if (g.status === 'passed' || g.status === 'locked') return
        get().updateBrief(novelId, {
          chapterGates: {
            ...cur.chapterGates,
            [key]: { ...g, status: 'drafting' },
          },
        })
      },
      applyGateResult: (novelId, chapterIndex, review, summaryIfPass) => {
        const cur = get().getBrief(novelId)
        const key = String(chapterIndex)
        const g = cur.chapterGates[key] || { status: 'review' as const, summary: '' }
        if (review.passed) {
          let next: ThesisBrief = {
            ...cur,
            updatedAt: new Date().toISOString(),
            chapterGates: {
              ...cur.chapterGates,
              [key]: {
                status: 'passed',
                summary: summaryIfPass.trim() || g.summary,
                lastReview: review,
              },
            },
          }
          next = unlockNextChapter(next, chapterIndex)
          next.stage = deriveStage(next)
          set({ byNovelId: { ...get().byNovelId, [novelId]: next } })
        } else {
          get().updateBrief(novelId, {
            chapterGates: {
              ...cur.chapterGates,
              [key]: {
                ...g,
                status: 'needs_revision',
                lastReview: review,
              },
            },
          })
        }
      },
      resetBrief: (novelId) => {
        const { [novelId]: _, ...rest } = get().byNovelId
        set({ byNovelId: rest })
      },
    }),
    {
      name: 'thesis-brief-v1',
      storage: createJSONStorage(() => localStorage),
    },
  ),
)
